import React, { useState, useEffect } from 'react';
import { Button, Input, Collapse, Popconfirm, notification } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddCircleIcon, CheckboxCheckedIcon, CheckboxUncheckedIcon, ChevronDownIcon, EditIcon, ChevronUpIcon, BinIcon, FileIcon, WarningReviewIcon } from 'components/Elements/CustomSVGIcon';
import './FormDetails.scss';
import PropTypes from 'prop-types';
import { DateTime, ShiftTime, VisitorRequestReview, Visitor } from 'core/visitRequestReview/types';
import type { CollapseProps } from 'antd';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { addComment, downloadAttachment } from 'core/visitRequestReview/visitRequestReviewApi';
import { VisitorRequest } from 'core/visitRequestForm/types';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING } from 'core/utils/value';
import useIsGuest from 'core/guestBooking/useIsGuest';
import { AppDispatch } from '../../../core/types';
import { MAX_LONG_TEXT_LENGTH } from '../../../core/constants';

// How many shifts a "Dates and Times" group shows before the dropdown while
// collapsed. The rest are revealed by expanding the group.
const PREVIEW_SHIFT_COUNT = 3;

interface FormDetailsProps {
    details: VisitorRequestReview | VisitorRequest;
    onPressTerm?: () => void;
    id?: string | undefined;
    signedBy?: string;
    onAcceptTerm?: (value: boolean) => void;
    onRemoveDate?: (id: number) => void;
    amending?: boolean;
    encryptedId?: string;
    isCostApproverByPass?: boolean;
    hasCostApprove?: boolean;
    onEmailChange?: (email: string) => void;
    userEmail?: string;
    onReviewVisitor?: (() => void) | undefined;
    // Admin-only reveal of each visitor's phone/email on the view page.
    canRevealContact?: boolean;
    // Retail general/access hides the shift end time (defaults to the site's
    // open-day closing time), showing only the start time.
    hideShiftEndTime?: boolean;
}

const FormDetails: React.FC<FormDetailsProps> = ({ onPressTerm, details, id, signedBy, onAcceptTerm, onRemoveDate, isCostApproverByPass, hasCostApprove, onEmailChange, userEmail, onReviewVisitor, canRevealContact = false, hideShiftEndTime = false }) => {
    const [showAddComment, setShowAddComment] = useState<boolean>(false);
    const [newComment, setNewComment] = useState<string>('');
    const [isSigned, setIsSigned] = useState(false);
    const [showAllVisitors, setShowAllVisitors] = useState<boolean>(false);
    const [showAllVisitorsOnSite, setShowAllVisitorsOnSite] = useState<boolean>(false);
    const [revealedVisitorIds, setRevealedVisitorIds] = useState<number[]>([]);
    const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>([]);

    const navigation = useNavigate();
    const { state: navigationState } = useLocation();
    const url = new URL(window.location.href);
    const { pathname } = url;
    // startsWith — `pathname.includes('visitor-request')` also matched the
    // post-submit view route `/visitor-view-request/...` and rendered the
    // form-only Declaration + edit/delete icons there. The guest booking flow
    // (/booking/:token/...) is also treated as the request form so the
    // declaration shows and the cost-approval / edit-only view bits don't.
    const visitorRequestForm = pathname.startsWith('/visitor-request/') || pathname.includes('/booking/');
    const accessRequestForm = pathname.startsWith('/access-request/');
    const guardingRequestForm = pathname.startsWith('/guarding-request/');
    const isAccessRequest = accessRequestForm || ('type' in details && details.type === REQUEST_TYPE_ACCESS) || !!details.accessTypeId;
    const isGuardingRequest = guardingRequestForm || ('type' in details && details.type === REQUEST_TYPE_GUARDING);
    // Both the guest booking flow (/booking/) and the guest view of a submitted
    // request (/guest/) are unauthenticated: hide the on-behalf/cost-approval
    // and comment-adding bits that assume a logged-in user.
    const isGuest = useIsGuest();
    const dispatch = useDispatch<AppDispatch>();

    const numberOfShifts = details?.dateAndTime?.reduce((acc, group) => acc + group.shifts.length, 0);

    // Emergency Request bookings flag one or more dates as time critical.
    const isEmergencyRequest = details?.dates?.some((date) => date.bookingTimeCritical) ?? false;

    // Groups start collapsed — the header already shows up to PREVIEW_SHIFT_COUNT
    // shifts, so expanding is only needed to reveal the overflow.
    useEffect(() => {
        setDefaultExpandedKeys([]);
    }, [details?.dateAndTime]);

    const handleCollapseChange = (activeKeys: string | string[]) => {
        setDefaultExpandedKeys(Array.isArray(activeKeys) ? activeKeys : [activeKeys]);
    };

    const handleSignDeclaration = () => {
        setIsSigned(!isSigned);
        if (onAcceptTerm) {
            onAcceptTerm(!isSigned);
        }
    };

    const handleAddCommentClick = () => {
        setShowAddComment(!showAddComment);
    };

    const handleCancelCommentClick = () => {
        setShowAddComment(false);
    };

    const handleNewCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewComment(e.target.value);
    };

    const handleSubmitNewComment = () => {
        // Users habitually paste the work details into a comment. Block a
        // comment that's identical to the work details (trimmed, case-insensitive)
        // so the same content isn't entered twice.
        const trimmedComment = newComment.trim();
        const workDetails = (details.workDetails?.description ?? '').trim();
        if (trimmedComment !== '' && workDetails !== ''
            && trimmedComment.toLowerCase() === workDetails.toLowerCase()) {
            notification.error({
                message: 'Duplicate comment',
                description: 'This comment is the same as the work details. Please add different information, or leave the comment blank.',
            });
            return;
        }

        if (details.encryptedId && newComment) {
            dispatch(addComment({ id: details.encryptedId.toString(), newComment }));
        }
        setNewComment('');
        setShowAddComment(false);
    };

    const handleShowMoreVisitors = () => {
        setShowAllVisitors(!showAllVisitors);
    };

    const handleShowMoreVisitorsOnSite = () => {
        setShowAllVisitorsOnSite(!showAllVisitorsOnSite);
    };

    // The grouped shift rows only carry a formatted date label, so map each
    // shift back to its raw date (via its id in the flat `dates` list) to decide
    // which shifts are upcoming — the preview favours the current/future dates.
    const startOfTodayMs = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    })();
    const shiftTimeById = new Map<number, number>();
    (details?.dates || []).forEach((date) => {
        if (date.id == null || date.date == null) {
            return;
        }
        const raw = typeof date.date === 'string' ? date.date : (date.date as { valueOf(): number }).valueOf();
        const ms = new Date(raw).getTime();
        if (!Number.isNaN(ms)) {
            shiftTimeById.set(date.id, ms);
        }
    });

    // Split a group's shifts into the header preview and the collapsible
    // full chronological list. Preview = the next few upcoming shifts (today or
    // later); if the whole group is in the past, fall back to the most recent
    // few — mirroring the ViewTable summary. Shifts with no known date keep
    // source order.
    const splitGroupShifts = (shifts: ShiftTime[]) => {
        const ordered = [...shifts].sort((a, b) => {
            const ta = shiftTimeById.get(a.id);
            const tb = shiftTimeById.get(b.id);
            if (ta == null && tb == null) return 0;
            if (ta == null) return 1;
            if (tb == null) return -1;
            return ta - tb;
        });
        // A short group fits in the header as it is, so show every shift —
        // filtering to upcoming would hide a past date behind an expander for
        // no gain (e.g. a three-night booking that started yesterday).
        if (ordered.length <= PREVIEW_SHIFT_COUNT) {
            return { preview: ordered, ordered };
        }

        const isUpcoming = (shift: ShiftTime) => {
            const ms = shiftTimeById.get(shift.id);
            return ms != null && ms >= startOfTodayMs;
        };
        const upcoming = ordered.filter(isUpcoming);
        const anyKnown = ordered.some((shift) => shiftTimeById.has(shift.id));

        let preview: ShiftTime[];
        if (upcoming.length > 0) {
            preview = upcoming.slice(0, PREVIEW_SHIFT_COUNT);
        } else if (anyKnown) {
            // All past — preview the most recent shifts.
            preview = ordered.slice(-PREVIEW_SHIFT_COUNT);
        } else {
            preview = ordered.slice(0, PREVIEW_SHIFT_COUNT);
        }
        return { preview, ordered };
    };

    const renderExpandIcon = ({ isActive, panelKey }: { isActive?: boolean; panelKey?: string }) => {
        // Find the group by panelKey to determine if it should show the arrow.
        // The test is what the header actually previews, not the group size: a
        // group of PREVIEW_SHIFT_COUNT shifts previews fewer once some are in
        // the past, and those past shifts are only reachable via the arrow.
        const group = details?.dateAndTime?.find((g: DateTime) => g.shiftType === panelKey);
        if (group && splitGroupShifts(group.shifts).preview.length >= group.shifts.length) {
            return null; // No overflow — the header already shows every shift
        }

        if (isActive) {
            return <ChevronUpIcon />;
        }
        return <ChevronDownIcon />;
    };

    const renderExtraIcon = (step:number) => {
        // Per-section edit jumps aren't wired for the guest flow (no id/guest
        // routes for every step), so guests navigate with Back instead.
        if (visitorRequestForm && !isGuest) {
            return (
                <Button type="link" className="edit-button" onClick={() => navigation(`/visitor-request/${id}/step/${step}`, { state: navigationState })}>
                    <EditIcon />
                </Button>
            );
        }
        if (accessRequestForm) {
            return (
                <Button type="link" className="edit-button" onClick={() => navigation(`/access-request/${id}/step/${step}`, { state: navigationState })}>
                    <EditIcon />
                </Button>
            );
        }
        if (guardingRequestForm) {
            return (
                <Button type="link" className="edit-button" onClick={() => navigation(`/guarding-request/${id}/step/${step}`, { state: navigationState })}>
                    <EditIcon />
                </Button>
            );
        }
        return null;
    };

    const handleAttachmentClick = (_id: number | undefined | string, name: string) => {
        if (id && _id) {
            dispatch(downloadAttachment({
                id: id.toString(),
                attachmentId: _id.toString(),
                fileName: name,
            }));
        }
    };

    const renderShiftRow = (shift: ShiftTime) => (
        <div key={shift.id}>
            <div className="date-time-content">
                <div className="date-shift-content">
                    <div className="date-shift-day">
                        <div style={{ width: '30%' }}>{shift.times.dates}</div>
                        <div className="single-shift">{shift.times.startTime}{hideShiftEndTime ? '' : ` - ${shift.times.endTime}`}</div>
                        {(visitorRequestForm || accessRequestForm || guardingRequestForm) && (
                            <div className="date-extra-button">
                                {renderExtraIcon(2)}
                                <Popconfirm
                                    disabled={numberOfShifts === 1}
                                    title="Are you sure you want to remove this date?"
                                    onConfirm={() => onRemoveDate && onRemoveDate(shift.id)}
                                    okText="Yes"
                                    cancelText="No">
                                    <Button type="link" className="bin-button">
                                        <BinIcon s={numberOfShifts === 1 ? '#C6C6C6' : '#D90429'} />
                                    </Button>
                                </Popconfirm>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const dateAndTime: CollapseProps['items'] = details?.dateAndTime && details.dateAndTime.map((group: DateTime) => {
        // Collapsed: the header shows up to PREVIEW_SHIFT_COUNT upcoming shifts.
        // Expanded: that preview is hidden (via CSS) and the body shows EVERY
        // shift in chronological order.
        const { preview: previewShifts, ordered: allShifts } = splitGroupShifts(group.shifts);
        const hasOverflow = allShifts.length > previewShifts.length;
        // Only the arrow toggles, so the shift rows / action buttons in the
        // header stay clickable without collapsing the group.
        const collapsible: 'icon' | 'disabled' = hasOverflow ? 'icon' : 'disabled';

        return {
            key: group.shiftType,
            className: hasOverflow ? '' : 'no-arrow-collapse',
            collapsible,
            label: (
                <div className="shift-group-header">
                    <span className="shift-group-title">{group.shiftType}</span>
                    <div className="shift-group-preview">
                        {previewShifts.map(renderShiftRow)}
                    </div>
                </div>
            ),
            children: hasOverflow ? allShifts.map(renderShiftRow) : null,
        };
    }) || [];

    const toggleRevealContact = (visitorId: number) => {
        setRevealedVisitorIds((ids) => (ids.includes(visitorId) ? ids.filter((v) => v !== visitorId) : [...ids, visitorId]));
    };

    const renderRevealContact = (detail: Visitor) => {
        if (!canRevealContact || detail.id == null || !(detail.email || detail.phoneNumber)) {
            return null;
        }
        const revealed = revealedVisitorIds.includes(detail.id);
        return (
            <button
                type="button"
                className="reveal-contact-button"
                aria-label={revealed ? 'Hide contact details' : 'Reveal contact details'}
                onClick={() => toggleRevealContact(detail.id as number)}>
                {revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </button>
        );
    };

    const renderRevealedContact = (detail: Visitor) => {
        if (!canRevealContact || detail.id == null || !revealedVisitorIds.includes(detail.id)) {
            return null;
        }
        return (
            <span className="visitor-contact">
                {detail.phoneNumber && <span>Phone: {detail.phoneNumber}</span>}
                {detail.email && <span>Email: {detail.email}</span>}
            </span>
        );
    };

    const renderVisitorsList = (visitorDetail: VisitorRequestReview['visitors']) => {
        let orderedVisitors: VisitorRequestReview['visitors'] = [];

        if (visitorDetail && visitorDetail.length !== 0) {
            orderedVisitors = [...visitorDetail].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }

        const visitorsToShow = showAllVisitors ? orderedVisitors : orderedVisitors?.slice(0, 10);
        const hasMoreVisitors = orderedVisitors && orderedVisitors.length > 10;
        const invalidNsipVisitors = visitorDetail?.filter((v) => v.isNsip && !v.isValidNsip) || [];
        const hasInvalidNsip = invalidNsipVisitors.length > 0 && onReviewVisitor;

        return (
            <div className="visitor-list-wrapper">
                {onReviewVisitor ? (
                    <div className="nsip-visitor-list">
                        {visitorsToShow?.map((visitor) => (
                            <div key={visitor.id} className="nsip-visitor-row">
                                <span className="visitor-name">
                                    <span>
                                        {visitor.name}
                                        {visitor.companyName && <span className="company-name"> ({visitor.companyName})</span>}
                                        {renderRevealContact(visitor)}
                                    </span>
                                    {renderRevealedContact(visitor)}
                                </span>
                                {visitor.isNsip && !visitor.isValidNsip && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2B2D42', fontSize: 16, fontWeight: 500 }}>
                                        <WarningReviewIcon />
                                        Invalid NSIP
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ul className="review-visitor-list">
                        {visitorsToShow?.map((visitor) => (
                            <li key={visitor.id}>
                                <span className="visitor-name">
                                    <span>
                                        {visitor.name}
                                        {visitor.companyName && <span className="company-name"> ({visitor.companyName})</span>}
                                        {renderRevealContact(visitor)}
                                    </span>
                                    {renderRevealedContact(visitor)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {hasMoreVisitors && (
                    <div className="show-more-visitors">
                        <Button
                            type="link"
                            className="show-more-button"
                            onClick={handleShowMoreVisitors}>
                            {showAllVisitors ? 'Show less visitors' : `Show ${orderedVisitors.length - 10} more visitors`}
                        </Button>
                    </div>
                )}
                {hasInvalidNsip && (
                    <div className="nsip-review-card">
                        <p style={{ margin: '0 0 16px 0', color: '#2B2D42', lineHeight: 1.5 }}>
                            One or more of your visitors does not have a valid NSIP Reference.
                            Please review the visitor to be able to approve the request.
                        </p>
                        <Button
                            type="primary"
                            className="nsip-review-button"
                            onClick={() => { if (onReviewVisitor) onReviewVisitor(); }}>
                            Review Visitor
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderVisitors = (visitorDetail: VisitorRequestReview['visitors']) => {
        const visitorsToShow = showAllVisitors ? visitorDetail : visitorDetail?.slice(0, 5);
        const hasMoreVisitors = visitorDetail && visitorDetail.length > 5;
        const invalidNsipVisitors = visitorDetail?.filter((v) => v.isNsip && !v.isValidNsip) || [];
        const hasInvalidNsip = invalidNsipVisitors.length > 0 && onReviewVisitor;

        return (
            <div className="visitor-content-container">
                <div className="visitor-content-header-row">
                    <div className="detail-item">
                        Visitors
                    </div>
                    <div className="detail-item">
                        Check-in Time
                    </div>
                    <div className="detail-item">
                        Check Out Time
                    </div>
                </div>
                {visitorsToShow?.map((detail) => (
                    <div className="visitor-content-row" key={detail.id}>
                        <div className="detail-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span className="visitor-name">
                                <span>
                                    {detail?.name}
                                    {detail?.companyName && <span className="company-name"> ({detail.companyName})</span>}
                                    {renderRevealContact(detail)}
                                </span>
                                {renderRevealedContact(detail)}
                            </span>
                            {detail?.isNsip && !detail?.isValidNsip && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2B2D42', fontSize: 16, fontWeight: 500 }}>
                                    <WarningReviewIcon />
                                    Invalid NSIP
                                </span>
                            )}
                        </div>
                        <div className="detail-item">
                            {detail?.lastCheckedIn !== '-' ? dayjs(detail?.lastCheckedIn).format('HH:mm') : detail?.lastCheckedIn}
                        </div>
                        <div className="detail-item">
                            {detail?.lastCheckedOut !== '-' ? dayjs(detail?.lastCheckedOut).format('HH:mm') : detail?.lastCheckedOut}
                        </div>
                    </div>
                ))}
                {hasMoreVisitors && (
                    <div className="show-more-visitors">
                        <Button
                            type="link"
                            className="show-more-button"
                            onClick={handleShowMoreVisitors}>
                            {showAllVisitors ? 'Show less visitors' : `Show ${visitorDetail.length - 5} more visitors`}
                        </Button>
                    </div>
                )}
                {hasInvalidNsip && (
                    <div className="nsip-review-card">
                        <p style={{ margin: '0 0 16px 0', color: '#2B2D42', lineHeight: 1.5 }}>
                            One or more of your visitors does not have a valid NSIP Reference.
                            Please review the visitor to be able to approve the request.
                        </p>
                        <Button
                            type="primary"
                            className="nsip-review-button"
                            onClick={() => { if (onReviewVisitor) onReviewVisitor(); }}>
                            Review Visitor
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderSelectedSite = (selectedSite: VisitorRequestReview['site']) => (
        <div>
            <div className="detail-item">
                <div className="detail-label">
                    Site Type:
                </div>
                <div className="detail-value">
                    {selectedSite?.type}
                </div>
            </div>
            <div className="detail-item">
                <div className="detail-label">
                    Site Name:
                </div>
                <div className="detail-value">
                    {selectedSite?.name}
                </div>
            </div>
            <div className="detail-item">
                <div className="detail-label">
                    Site Address:
                </div>
                <div className="detail-value">
                    {selectedSite?.fullAddress}
                </div>
            </div>
        </div>
    );

    const renderAccessAreas = () => {
        // Distinct site areas selected across the request's site-access entries
        // (e.g. "Internal", "Compound"), so the reviewer can see which areas the
        // visit covers.
        const siteAccessEntries = (details?.siteAccess as Array<{ siteAreaNames?: string[] }> | null) || [];
        const selectedAreas = Array.from(
            new Set(siteAccessEntries.flatMap((entry) => entry.siteAreaNames || [])),
        ).filter(Boolean);

        return (
            <div className="detail-item">
                <div className="detail-label">
                    Access Area(s):
                </div>
                <div className="detail-value">
                    {selectedAreas.length ? selectedAreas.join(', ') : 'N/A'}
                </div>
            </div>
        );
    };

    const renderVisitorsOnSite = (visitorsOnSite: VisitorRequestReview['lastCheckedIn']) => {
        const visitorsToShow = showAllVisitorsOnSite ? visitorsOnSite : visitorsOnSite.slice(0, 5);
        const hasMoreVisitors = visitorsOnSite.length > 5;

        // Helper function to get company name for a visitor
        const getCompanyName = (visitorName: string) => {
            if (details.visitors && Array.isArray(details.visitors)) {
                const visitors = details.visitors as Visitor[];
                const visitor = visitors.find((v: Visitor) => v.name === visitorName);
                return visitor?.companyName;
            }
            return undefined;
        };

        return (
            <div className="rounded-container">
                <table>
                    <thead>
                        <tr>
                            <th>Visitors</th>
                            <th>Check-in Time</th>
                            <th>Check-out Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visitorsToShow.map((visitor) => {
                            const companyName = getCompanyName(visitor.visitorName);
                            return (
                                <tr key={visitor.visitorName}>
                                    <td>
                                        {visitor.visitorName}
                                        {companyName && <span className="company-name"> ({companyName})</span>}
                                    </td>
                                    <td>{visitor.lastCheckedIn}</td>
                                    <td>{visitor.lastCheckedOut}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {hasMoreVisitors && (
                    <div className="show-more-visitors">
                        <Button
                            type="link"
                            className="show-more-button"
                            onClick={handleShowMoreVisitorsOnSite}>
                            {showAllVisitorsOnSite ? 'Show less visitors' : `Show ${visitorsOnSite.length - 5} more visitors`}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const getNsipReference = () => {
        if (details.visitors && Array.isArray(details.visitors)) {
            const visitorWithNsip = (details.visitors as Visitor[]).find((v: Visitor) => v.nsipReference);
            return visitorWithNsip?.nsipReference;
        }
        return undefined;
    };

    const renderWorkType = () => (
        <div>
            <div className="detail-item">
                <div className="detail-label">
                    Work Types:
                </div>
                <div className="detail-value">
                    {details.workTypeName}
                </div>
            </div>
            <div className="detail-item">
                <div className="detail-label">
                    Description of Works:
                </div>
                <div className="detail-value">
                    {details.workDetails?.description || 'N/A'}
                </div>
            </div>
            {accessRequestForm && getNsipReference() && (
                <div className="detail-item">
                    <div className="detail-label">
                        NSIP Reference:
                    </div>
                    <div className="detail-value">
                        {getNsipReference()}
                    </div>
                </div>
            )}
            <div className="detail-item">
                <div className="detail-label">
                    Work Reference:
                </div>
                <div className="detail-value">
                    {details.workDetails?.workReference || 'N/A'}
                </div>
            </div>
            <div className="detail-sub-item">
                <div className="detail-label">
                    Supporting Documentation
                </div>
                <div className="detail-sub-value">
                    {details.attachments ? (
                        details.attachments.map((attachment) => (
                            <div className="file-detail-content" key={attachment.id} onClick={() => handleAttachmentClick(attachment?.id, attachment.originalFilename)}>
                                <div className="file-detail">
                                    <div className="file-name">
                                        <FileIcon />
                                        <span>{attachment.originalFilename}</span>
                                    </div>
                                    {/* <div className="remove-file-icon" onClick={() => handleRemoveDocument(attachment.id)}>
                                        <CancelIcon s="#C0D1D8" />
                                    </div> */}
                                    <div className="border" />
                                </div>
                            </div>
                        ))
                    ) : ('N/A')}
                </div>
            </div>
        </div>
    );

    const renderAccessAnswerValue = (answer: { type: string; allowMultiple: boolean; answer: string }) => {
        if (!answer.answer && answer.answer !== '') return '-';
        if (answer.answer === '') return '-';

        if (answer.type === 'boolean') {
            return answer.answer === 'true' || answer.answer === '1' ? 'Yes' : 'No';
        }

        if (answer.type === 'select' && answer.allowMultiple) {
            try {
                const values = typeof answer.answer === 'string'
                    ? JSON.parse(answer.answer)
                    : answer.answer;
                if (Array.isArray(values) && values.length > 0) {
                    return values.join(', ');
                }
                return '-';
            } catch {
                return answer.answer || '-';
            }
        }

        return answer.answer || '-';
    };

    const renderAccessAttachments = () => {
        const accessAttachments = 'accessAttachments' in details ? details.accessAttachments : undefined;
        if (!accessAttachments || accessAttachments.length === 0) {
            return null;
        }

        return (
            <div className="detail-sub-item">
                <div className="detail-label">
                    Supporting Documentation
                </div>
                <div className="detail-sub-value">
                    {accessAttachments.map((attachment) => (
                        <div className="file-detail-content" key={attachment.id} onClick={() => handleAttachmentClick(attachment?.id, attachment.originalFilename)}>
                            <div className="file-detail">
                                <div className="file-name">
                                    <FileIcon />
                                    <span>{attachment.originalFilename}</span>
                                </div>
                                <div className="border" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderAccessSection = () => {
        const hasAnswers = 'accessTypeAnswers' in details && !!details.accessTypeAnswers?.length;
        const hasAccessAttachments = 'accessAttachments' in details && details.accessAttachments && details.accessAttachments.length > 0;
        const hasAccessType = 'accessType' in details && !!details.accessType;

        if (!hasAnswers && !hasAccessAttachments && !hasAccessType) {
            return null;
        }

        return (
            <div className="detail-content">
                <div className="header-form-container">
                    <h2>Access</h2>
                    {renderExtraIcon(4)}
                </div>
                <div className="content-container">
                    {hasAccessType && (
                        <div className="detail-item">
                            <div className="detail-label">
                                Access Type:
                            </div>
                            <div className="detail-value">
                                {details.accessType}
                            </div>
                        </div>
                    )}
                    {hasAnswers && details.accessTypeAnswers?.map((answer) => (
                        <div className="detail-item" key={answer.questionId}>
                            <div className="detail-label">
                                {answer.label}:
                            </div>
                            <div className="detail-value">
                                {renderAccessAnswerValue(answer)}
                            </div>
                        </div>
                    ))}
                    {renderAccessAttachments()}
                </div>
            </div>
        );
    };

    const renderComment = (comments: VisitorRequestReview['comments']) => (
        comments?.map((comment) => (
            <div className="content-container" key={comment.id}>
                <div className="detail-item">
                    <div className="detail-label">
                        <span>{comment.commenter}:</span>
                    </div>
                    <div className="detail-value">
                        <span>{comment.comment}</span>
                    </div>
                </div>
                <div className="detail-comment-time">
                    <span>{comment.createdDate} at {comment.createdTime}</span>
                </div>
            </div>
        ))
    );

    return (
        <div style={{ display: 'grid', justifyContent: 'center', paddingBottom: 100, position: 'relative' }}>
            <div className="form-header">
                <h1>{(() => { if (visitorRequestForm) return 'Review Visitor Request'; if (guardingRequestForm) return 'Review Guarding Request'; if (accessRequestForm) return 'Review Access Request'; return 'Full Request Details'; })()}</h1>
                {isEmergencyRequest && (<div className="emergency-request-flag"> <WarningReviewIcon /> <div className="emergency-request-flag-text">Emergency Request</div></div>)}
                {(!isCostApproverByPass && !hasCostApprove
                    // Hide on any form page (form has its own cost handling)
                    && !visitorRequestForm && !accessRequestForm && !guardingRequestForm
                    // Access requests never need cost approval (AccessReviewStep
                    // always saves has_cost_approve). Guarding does, on the same
                    // terms as a visitor request (GuardingReviewStep).
                    && !isAccessRequest
                    // Guests (unauthenticated) never see the cost-approval prompt.
                    && !isGuest
                ) && (<div className="cost-approver-bypass"> <WarningReviewIcon /> <div className="cost-approver-bypass-text">Cost Approval is Required</div></div>)}
            </div>
            <div className="detail-content">
                <div className="header-form-container">
                    <h2>Selected Site</h2>
                </div>
                <div className="content-container">
                    {renderSelectedSite(details.site)}
                    {/* Access Area(s) only apply to visitor requests. Access requests
                        use "Access Type" (rendered in renderAccessSection) instead, and
                        guarding requests have neither, so don't show the "N/A" row. */}
                    {!isAccessRequest && !isGuardingRequest && renderAccessAreas()}
                </div>
            </div>

            { !isGuardingRequest && ('lastCheckedIn' in details && details.lastCheckedIn.length > 0) && (
                <div className="detail-content">
                    <div className="header-form-container">
                        <h2>Visitors on Site</h2>
                        {renderExtraIcon(4)}
                    </div>
                    <div className="content-container">
                        {renderVisitorsOnSite(details.lastCheckedIn)}
                    </div>
                </div>
            )}

            <div className="detail-content">
                <div className="header-form-container">
                    <h2>Visitor Added</h2>
                    {renderExtraIcon(3)}
                </div>
                {onAcceptTerm ? renderVisitorsList(details.visitors) : renderVisitors(details.visitors)}
            </div>

            <div className="detail-content">
                <h2>Dates and Times</h2>
                <div className="content-container">
                    <Collapse
                        expandIconPosition="end"
                        expandIcon={renderExpandIcon}
                        activeKey={defaultExpandedKeys}
                        onChange={handleCollapseChange}
                        items={dateAndTime || []} />
                    {/* Access requests are not billed by the hour; guarding and
                        visitor requests both are, and the API stores the same
                        figure shown here. */}
                    {!isAccessRequest && (
                        <div className="total-hour">
                            <div>Total Hours Billed:&nbsp;</div>
                            <div>{details.totalHours ?? 0}</div>
                        </div>
                    )}
                    {details.numberOfOfficers !== undefined && details.numberOfOfficers > 1 && (
                        <div className="total-hour">
                            <div>Multi Officer Request:&nbsp;</div>
                            <div>{details.numberOfOfficers}</div>
                        </div>
                    )}
                    <div className="total-hour">
                        <div>{(isGuardingRequest || isAccessRequest) ? 'Cost Centre:' : 'Cost Code:'}&nbsp;</div>
                        <div>{details.costCode?.costCentre || 'N/A'}</div>
                    </div>
                </div>
            </div>

            {!isGuardingRequest && renderAccessSection()}

            <div className="detail-content">
                <div className="header-form-container">
                    <h2>{(isAccessRequest || isGuardingRequest) ? 'Job Details' : 'Work Type'}</h2>
                    {renderExtraIcon((isAccessRequest || isGuardingRequest) ? 1 : 5)}
                </div>
                <div className="content-container">
                    {renderWorkType()}
                </div>
            </div>
            {(!visitorRequestForm && !accessRequestForm && !guardingRequestForm) ? (
                <div className="detail-content">
                    <h2>Comments</h2>
                    <div className="comments-content">
                        {renderComment(details.comments)}
                        {/* Guests (unauthenticated) can read comments but not add them. */}
                        {!isGuest && (showAddComment ? (
                            <div>
                                <h3 className="heading">Add your comment here</h3>
                                <textarea className="comment-textarea" maxLength={MAX_LONG_TEXT_LENGTH} onChange={handleNewCommentChange} />
                                <div className="button-wrapper">
                                    <button className="save-button" type="button" onClick={handleSubmitNewComment}>Submit</button>
                                    <button className="cancel-button" type="button" onClick={handleCancelCommentClick}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div className="add-comment" onClick={handleAddCommentClick}>
                                <div className="icon-wrapper">
                                    <AddCircleIcon />
                                </div>
                                <span className="add-comment-text">Add a Comment</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="declaration-content">
                    <div className="grey-background" />
                    <h2>Declaration</h2>
                    <div className="content-container">
                        <div className="sign-item">
                            {!isGuest && (
                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Email Address</div>
                                    <div style={{ fontSize: 14, color: '#6C757D', marginBottom: 12 }}>
                                        You can submit this request on behalf of a non-authenticated user. Please provide their email address.
                                    </div>
                                    <Input
                                        type="email"
                                        placeholder="Enter email address"
                                        value={userEmail}
                                        onChange={(e) => onEmailChange && onEmailChange(e.target.value)}
                                        style={{
                                            fontSize: 16,
                                            width: 400,
                                        }} />
                                </div>
                            )}
                            <div className="detail-label">
                                <div onClick={handleSignDeclaration}>
                                    {isSigned ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />}
                                </div>
                                <span style={{ paddingLeft: 10 }}>I declare that all applicants understand and accept the Vodafone{'\n'}
                                    <span className="term-condition" onClick={() => onPressTerm && onPressTerm()} style={{ fontWeight: 700, textDecorationLine: 'underline', cursor: 'pointer' }}>
                                        Terms and Conditions
                                    </span> of access.
                                </span>
                            </div>
                            <div className="signed-container">
                                <div className="signed-title">Signed</div>
                                <Input
                                    disabled
                                    value={signedBy}
                                    style={{
                                        fontSize: 18,
                                        width: 400,
                                        backgroundColor: '#EDF2F4',
                                        border: 'none' }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

FormDetails.defaultProps = {
    onPressTerm: () => console.log('onPressTerm'),
    id: undefined,
    signedBy: '',
    onAcceptTerm: () => console.log('onAcceptTerm'),
    onRemoveDate: () => console.log('onRemoveDate'),
    amending: false,
    encryptedId: '',
    isCostApproverByPass: false,
    hasCostApprove: false,
    onEmailChange: () => console.log('onEmailChange'),
    userEmail: '',
    canRevealContact: false,
};

FormDetails.propTypes = {
    onPressTerm: PropTypes.func,
    id: PropTypes.string,
    signedBy: PropTypes.string,
    onAcceptTerm: PropTypes.func,
    onRemoveDate: PropTypes.func,
    amending: PropTypes.bool,
    encryptedId: PropTypes.string,
    isCostApproverByPass: PropTypes.bool,
    hasCostApprove: PropTypes.bool,
    onEmailChange: PropTypes.func,
    userEmail: PropTypes.string,
    canRevealContact: PropTypes.bool,
};

export default FormDetails;
